import { CommonModule } from '@angular/common'
import { Component, DestroyRef, ElementRef, ViewChild, inject, output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormsModule } from '@angular/forms'
import { animationFrameScheduler, timer } from 'rxjs'
import { InternationalizationService } from '../../../../services/internationalization.service'
import { ServerUrlService } from '../../../../services/server-url.service'
import { ChatAttachmentEntity, ChatMessageEntity } from '../../interfaces/chat.interface'
import { MessageChatStateService } from '../../services/message-chat-state.service'

interface MessageGroup {
  dateLabel: string
  messages: ChatMessageEntity[]
}

@Component({
  selector: 'app-chat-messages',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './chat-messages.component.html',
  styleUrl: './chat-messages.component.scss',
})
export class ChatMessagesComponent {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef<HTMLElement>

  private readonly destroyRef = inject(DestroyRef)

  constructor(
    protected state: MessageChatStateService,
    private serverUrl: ServerUrlService,
    protected translateService: InternationalizationService
  ) {}

  readonly loadMore = output<void>()
  readonly deleteMessage = output<ChatMessageEntity>()
  readonly openLightbox = output<ChatAttachmentEntity>()
  readonly saveEdit = output<{ messageId: number; content: string }>()

  readonly confirmingDeleteId = signal<number | null>(null)
  readonly editingMessageId = signal<number | null>(null)
  readonly editingText = signal<string>('')

  private previousScrollHeight = 0
  protected showScrollButton = false
  private nearBottomThreshold = 150
  private resizeObserver?: ResizeObserver
  private trackMediaLoads = false

  onDeleteClick(msg: ChatMessageEntity): void {
    this.editingMessageId.set(null)
    this.editingText.set('')
    this.confirmingDeleteId.set(msg.id)
  }

  onConfirmDelete(msg: ChatMessageEntity): void {
    this.confirmingDeleteId.set(null)
    this.deleteMessage.emit(msg)
  }

  onEditClick(msg: ChatMessageEntity): void {
    this.confirmingDeleteId.set(null)
    this.editingMessageId.set(msg.id)
    this.editingText.set(msg.content ?? '')
  }

  onSaveInlineEdit(): void {
    const msgId = this.editingMessageId()
    const text = this.editingText().trim()
    if (msgId && text) {
      this.saveEdit.emit({ messageId: msgId, content: text })
    }
    this.editingMessageId.set(null)
    this.editingText.set('')
  }

  cancelInlineEdit(): void {
    this.editingMessageId.set(null)
    this.editingText.set('')
  }

  onScroll(): void {
    const el = this.scrollContainer.nativeElement

    this.confirmingDeleteId.set(null)
    this.showScrollButton = !this.isNearBottom()

    if (this.showScrollButton) {
      const dist = el.scrollHeight - el.scrollTop - el.clientHeight
      if (dist > 500) {
        this.trackMediaLoads = false
      }
      this.resizeObserver?.disconnect()
    }

    if (el.scrollTop === 0 && this.state.hasMoreMessages() && !this.state.loadingMore()) {
      this.previousScrollHeight = el.scrollHeight
      this.loadMore.emit()
    }
  }

  private isNearBottom(): boolean {
    if (!this.scrollContainer) {
      return true
    }
    const el = this.scrollContainer.nativeElement
    return el.scrollHeight - el.scrollTop - el.clientHeight < this.nearBottomThreshold
  }

  restoreScrollAfterPrepend(): void {
    timer(0, animationFrameScheduler)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const el = this.scrollContainer.nativeElement
        el.scrollTop = el.scrollHeight - this.previousScrollHeight
      })
  }

  scrollToBottom(): void {
    timer(0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.scrollContainer) {
          const el = this.scrollContainer.nativeElement
          el.scrollTop = el.scrollHeight
          this.showScrollButton = false
        }
      })
  }

  scrollToBottomAndWaitForMedia(): void {
    this.trackMediaLoads = true
    this.scrollToBottom()
    this.resizeObserver?.disconnect()

    timer(0)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const inner = this.scrollContainer?.nativeElement.querySelector('.messages-inner')
        if (!inner) {
          return
        }

        const el = this.scrollContainer.nativeElement
        this.resizeObserver = new ResizeObserver(() => {
          el.scrollTop = el.scrollHeight
        })
        this.resizeObserver.observe(inner)

        timer(5000)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => this.resizeObserver?.disconnect())
      })
  }

  onNewMessage(isOwnMessage = false): void {
    if (isOwnMessage || this.isNearBottom()) {
      this.trackMediaLoads = true
      this.scrollToBottom()
    } else {
      this.showScrollButton = true
    }
  }

  onMediaLoaded(): void {
    if (!this.trackMediaLoads) {
      return
    }
    const el = this.scrollContainer?.nativeElement
    if (!el) {
      return
    }
    el.scrollTop = el.scrollHeight
  }

  get messageGroups(): MessageGroup[] {
    const messages = this.state.sortedMessages()
    const groups: MessageGroup[] = []
    let currentDate = ''

    for (const msg of messages) {
      const label = this.getDateLabel(msg.sentAt)
      if (label !== currentDate) {
        currentDate = label
        groups.push({ dateLabel: label, messages: [msg] })
      } else {
        groups[groups.length - 1].messages.push(msg)
      }
    }

    return groups
  }

  isOwnMessage(msg: ChatMessageEntity): boolean {
    return msg.userId === this.state.currentUserId()
  }

  formatTime(dateStr: string): string {
    const date = new Date(dateStr)
    return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  isImage(att: ChatAttachmentEntity): boolean {
    return att.mimeType.startsWith('image/')
  }

  isVideo(att: ChatAttachmentEntity): boolean {
    return att.mimeType.startsWith('video/')
  }

  getFileSize(bytes: number): string {
    if (bytes < 1024) {
      return `${bytes}B`
    }
    if (bytes < 1048576) {
      return `${(bytes / 1024).toFixed(0)}KB`
    }
    return `${(bytes / 1048576).toFixed(1)}MB`
  }

  getAttachmentUrl(att: ChatAttachmentEntity): string {
    return `${this.serverUrl.url}/uploads/${att.filePath}`
  }

  private getDateLabel(dateStr: string): string {
    const date = new Date(dateStr)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    if (date.toDateString() === today.toDateString()) {
      return this.translateService.getTranslation(1108)
    }
    if (date.toDateString() === yesterday.toDateString()) {
      return this.translateService.getTranslation(4156)
    }

    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    })
  }
}
